import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService } from '@telecom/shared/services/i18n';
import { type TaskDateFormatter } from '../task-date-formatter';
import { TimeZoneService } from '../time-zone-service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

@injectable()
export class TaskDateFormatterImpl implements TaskDateFormatter {
    constructor(
        @inject(I18nService)
        private _i18nService: I18nService,
        @inject(TimeZoneService)
        private _timeZoneService: TimeZoneService,
    ) {
        dayjs.extend(utc);
    }

    format(utcTimeStamp: number): string {
        const fmt = this._getDateTimeFormat();
        const dateFormat = this._getDateFormat();
        const todayFormat = this._getTodayFormat();
        const yesterdayFormat = this._getYesterdayFormat();
        // 获取客户端时区与UTC的偏移量
        const clientTimeZoneOffset = new Date().getTimezoneOffset();
        // 获取用户设置的时区与UTC的偏移量
        const utcOffset = -this._timeZoneService.getUTCOffset() || clientTimeZoneOffset;

        const value = dayjs().utcOffset(-utcOffset);
        const todayStr = value.format(dateFormat);
        const yesterdayStr = value.subtract(1, 'days').format(dateFormat);

        const timezoneTime = dayjs(dayjs.utc(utcTimeStamp).format())
            .utcOffset(-utcOffset)
            .format(fmt);
        const result = timezoneTime
            .replace(todayStr, todayFormat)
            .replace(yesterdayStr, yesterdayFormat);
        return result;
    }

    formatWithPattern(date: string | number, pattern: string): string {
        return dayjs(date).format(pattern);
    }

    isAfter(date1: string | number, date2: string | number): boolean {
        return dayjs(date1).isAfter(dayjs(date2));
    }

    isBefore(date1: string | number, date2: string | number): boolean {
        return dayjs(date1).isBefore(dayjs(date2));
    }

    compare(date1: string | number, date2: string | number): number {
        const d1 = dayjs(date1);
        const d2 = dayjs(date2);
        if (d1.isAfter(d2)) return 1;
        if (d1.isBefore(d2)) return -1;
        return 0;
    }

    now(): number {
        return Date.now();
    }

    private _getTodayFormat(): string {
        const todayFormat = this._i18nService.t('drafting.format.today');
        return todayFormat;
    }

    private _getYesterdayFormat(): string {
        const yesterdayFormat = this._i18nService.t('drafting.format.yesterday');
        return yesterdayFormat;
    }

    private _getDateFormat(): string {
        const dateFormat = this._i18nService.t('drafting.format.date');
        return dateFormat;
    }

    private _getTimeFormat(): string {
        const timeFormat = this._i18nService.t('drafting.format.time');
        return timeFormat;
    }

    private _getDateTimeFormat(): string {
        const dateFormat = this._getDateFormat();
        const timeFormat = this._getTimeFormat();
        return `${dateFormat} ${timeFormat}`;
    }
}
